import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  styled,
} from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { getAuthToken } from "../utils/session";

const EXPANDED_WIDTH = 248;
const COLLAPSED_WIDTH = 68;

const FONT_FAMILY = `
  Inter,
  Manrope,
  "SF Pro Display",
  "Segoe UI",
  Roboto,
  Arial,
  sans-serif
`;

const THEME = {
  page: "#07111F",
  sidebar: "#0A1626",
  sidebarDeep: "#07111F",
  surface: "rgba(255,255,255,0.055)",
  surfaceHover: "rgba(255,255,255,0.085)",
  active: "rgba(79,156,249,0.18)",
  activeBorder: "rgba(79,156,249,0.34)",

  border: "rgba(148,163,184,0.14)",
  borderStrong: "rgba(148,163,184,0.24)",

  text: "#F8FAFC",
  textSoft: "#D8E3F0",
  muted: "#8DA0B8",

  primary: "#4F9CF9",
  primarySoft: "rgba(79,156,249,0.16)",

  accent: "#D37D36",
  accentSoft: "rgba(211,125,54,0.16)",

  danger: "#FB7185",
  dangerSoft: "rgba(251,113,133,0.14)",
};

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ theme, selected, collapsed }) => ({
  minHeight: collapsed ? 42 : 42,
  margin: collapsed ? theme.spacing(0.45, 0.85) : theme.spacing(0.35, 1.1),
  padding: collapsed ? theme.spacing(0.65, 0) : theme.spacing(0.75, 1),
  borderRadius: collapsed ? 15 : 14,
  color: selected ? THEME.text : THEME.muted,
  justifyContent: collapsed ? "center" : "flex-start",
  transition: "all 180ms ease",
  position: "relative",
  overflow: "hidden",
  fontFamily: FONT_FAMILY,

  "& .MuiListItemIcon-root": {
    minWidth: 0,
    width: collapsed ? 36 : 34,
    height: collapsed ? 36 : 34,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    color: selected ? THEME.text : THEME.muted,
    backgroundColor: selected ? THEME.primarySoft : "rgba(255,255,255,0.035)",
    border: selected
      ? `1px solid ${THEME.activeBorder}`
      : "1px solid rgba(148,163,184,0.08)",
    transition: "all 180ms ease",
  },

  "& .MuiSvgIcon-root": {
    fontSize: collapsed ? 21 : 19,
  },

  "& .MuiListItemText-root": {
    marginLeft: 10,
  },

  "& .MuiListItemText-primary": {
    fontFamily: FONT_FAMILY,
    fontSize: "0.78rem",
    fontWeight: selected ? 750 : 600,
    color: selected ? THEME.text : THEME.textSoft,
    letterSpacing: "-0.01em",
  },

  ...(selected && {
    background:
      "linear-gradient(135deg, rgba(79,156,249,0.18), rgba(79,156,249,0.08))",
    boxShadow: "inset 0 0 0 1px rgba(79,156,249,0.18)",

    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 11,
      bottom: 11,
      width: 3,
      borderRadius: "0 20px 20px 0",
      background: `linear-gradient(180deg, ${THEME.primary}, ${THEME.accent})`,
    },
  }),

  "&:hover": {
    backgroundColor: selected ? THEME.active : THEME.surfaceHover,
    transform: collapsed ? "none" : "translateX(2px)",

    "& .MuiListItemIcon-root": {
      color: THEME.text,
      backgroundColor: selected
        ? "rgba(79,156,249,0.22)"
        : "rgba(79,156,249,0.12)",
      borderColor: "rgba(79,156,249,0.24)",
    },
  },
}));

const SectionTitle = ({ children, collapsed }) => {
  if (collapsed) {
    return <Box sx={{ height: 8 }} />;
  }

  return (
    <Typography
      variant="overline"
      sx={{
        display: "block",
        px: 2.5,
        pt: 1.35,
        pb: 0.45,
        color: THEME.muted,
        fontSize: "0.6rem",
        fontWeight: 800,
        letterSpacing: "0.13em",
        fontFamily: FONT_FAMILY,
      }}
    >
      {children}
    </Typography>
  );
};

const getUserInfoFromToken = () => {
  const token = getAuthToken();

  if (!token) {
    return {
      role: "",
      name: "",
      email: "",
      initials: "U",
      roleLabel: "Usuario",
    };
  }

  try {
    const decoded = jwtDecode(token);
    const role = decoded.role || "";
    const name = decoded.name || decoded.username || "";
    const email = decoded.sub || decoded.email || "";
    const displayName = name || email?.split("@")[0] || "Usuario";
    const initials = displayName.charAt(0).toUpperCase() || "U";
    const roleLabel = role.replace("ROLE_", "").toLowerCase() || "usuario";

    return {
      role,
      name: displayName,
      email,
      initials,
      roleLabel,
    };
  } catch (error) {
    console.error("Invalid token", error);
    return {
      role: "",
      name: "Usuario",
      email: "",
      initials: "U",
      roleLabel: "Usuario",
    };
  }
};

const getMenuSections = (role) => {
  if (role === "ROLE_ADMIN") {
    return [
      {
        title: "Administración",
        items: [
          { text: "General", icon: <HomeIcon />, path: "/admin-dashboard" },
          { text: "Usuarios", icon: <PersonIcon />, path: "/admin-dashboard/usuarios" },
          { text: "Preguntas", icon: <HelpOutlineIcon />, path: "/admin-dashboard/preguntas" },
          { text: "Procesos", icon: <SettingsIcon />, path: "/admin-dashboard/procesos" },
        ],
      },
    ];
  }

  return [
    {
      title: "Principal",
      items: [{ text: "Inicio", icon: <HomeIcon />, path: "/mis-autoevaluaciones" }],
    },
    {
      title: "Evaluación",
      items: [
        {
          text: "Nueva autoevaluación",
          icon: <DescriptionOutlinedIcon />,
          path: "/nueva-autoevaluacion",
        },
      ],
    },
  ];
};

const isItemSelected = (currentPath, itemPath) => {
  if (itemPath === "/admin-dashboard") {
    return currentPath === itemPath;
  }

  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
};

const Sidebar = ({ onWidthChange }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isMobile = useMediaQuery("(max-width:900px)");
  const isSmallHeight = useMediaQuery("(max-height:720px)");

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const user = useMemo(() => getUserInfoFromToken(), []);
  const menuSections = useMemo(() => getMenuSections(user.role), [user.role]);

  useEffect(() => {
    setCollapsed(!isMobile);
  }, [isMobile]);

  const handleLogout = () => {
    Cookies.remove("token");
    navigate("/");
  };

  const handleMobileClose = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerWidth = isMobile
    ? EXPANDED_WIDTH
    : collapsed
      ? COLLAPSED_WIDTH
      : EXPANDED_WIDTH;

  useEffect(() => {
    if (typeof onWidthChange === "function") {
      onWidthChange(isMobile ? 0 : drawerWidth);
    }
  }, [drawerWidth, isMobile, onWidthChange]);

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        color: THEME.text,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        fontFamily: FONT_FAMILY,
        background:
          "radial-gradient(circle at top left, rgba(79,156,249,0.16), transparent 34%), radial-gradient(circle at 100% 20%, rgba(211,125,54,0.10), transparent 30%), linear-gradient(180deg, #0B182A 0%, #07111F 100%)",
      }}
    >
      <Box
        sx={{
          height: collapsed ? 70 : 76,
          px: collapsed ? 1 : 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: `1px solid ${THEME.border}`,
          flexShrink: 0,
        }}
      >
        <Box
          component={Link}
          to="/mis-autoevaluaciones"
          onClick={handleMobileClose}
          sx={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <Box
  sx={{
    width: collapsed ? 42 : 44,
    height: collapsed ? 42 : 44,
    borderRadius: "16px",
    display: "grid",
    placeItems: "center",
    background: "#FFFFFF",
    border: "1px solid rgba(255,255,255,0.85)",
    boxShadow: "0 14px 28px rgba(0,0,0,0.22)",
    flexShrink: 0,
    overflow: "hidden",
  }}
>
            <Box
  component="img"
  src="/images/logo_simbolo.png"
  alt="DQPA"
  sx={{
    width: collapsed ? 34 : 36,
    height: collapsed ? 34 : 36,
    objectFit: "contain",
    display: "block",
  }}
/>
          </Box>

          {!collapsed && (
            <Box sx={{ ml: 1.15, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: THEME.text,
                  fontSize: "0.95rem",
                  fontWeight: 850,
                  letterSpacing: "-0.035em",
                  lineHeight: 1.05,
                  fontFamily: FONT_FAMILY,
                }}
              >
                DQPA
              </Typography>

              <Typography
                noWrap
                sx={{
                  color: THEME.muted,
                  fontSize: "0.66rem",
                  fontWeight: 600,
                  mt: 0.25,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Gobierno y gestión del dato
              </Typography>
            </Box>
          )}
        </Box>

        {!isMobile && !collapsed && (
          <Tooltip title="Contraer" placement="right" arrow>
            <IconButton
              onClick={() => setCollapsed(true)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "10px",
                color: THEME.muted,
                bgcolor: "rgba(255,255,255,0.045)",
                border: `1px solid ${THEME.border}`,
                transition: "all 180ms ease",
                "&:hover": {
                  bgcolor: "rgba(79,156,249,0.14)",
                  color: THEME.text,
                  borderColor: "rgba(79,156,249,0.32)",
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!isMobile && collapsed && (
        <Box
          sx={{
            px: 1,
            pt: 1,
            pb: 0.25,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Tooltip title="Expandir menú" placement="right" arrow>
            <IconButton
              onClick={() => setCollapsed(false)}
              sx={{
                width: 38,
                height: 34,
                borderRadius: "13px",
                color: THEME.textSoft,
                bgcolor: "rgba(255,255,255,0.045)",
                border: `1px solid ${THEME.border}`,
                transition: "all 180ms ease",
                "&:hover": {
                  bgcolor: "rgba(79,156,249,0.15)",
                  color: THEME.text,
                  borderColor: "rgba(79,156,249,0.30)",
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box
        component="nav"
        sx={{
          flex: 1,
          py: collapsed ? 0.5 : 0.8,
          overflowY: "auto",
          overflowX: "hidden",

          "&::-webkit-scrollbar": {
            width: 5,
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(148,163,184,0.22)",
            borderRadius: 99,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            bgcolor: "rgba(148,163,184,0.34)",
          },
        }}
      >
        {menuSections.map((section) => (
          <List key={section.title} disablePadding sx={{ mb: 0.4 }}>
            <SectionTitle collapsed={collapsed}>{section.title}</SectionTitle>

            {section.items.map((item) => {
              const selected = isItemSelected(location.pathname, item.path);

              return (
                <Tooltip
                  key={item.text}
                  title={collapsed ? item.text : ""}
                  placement="right"
                  arrow
                >
                  <StyledListItemButton
                    collapsed={collapsed}
                    selected={selected}
                    component={Link}
                    to={item.path}
                    onClick={handleMobileClose}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>

                    {!collapsed && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          noWrap: true,
                        }}
                      />
                    )}
                  </StyledListItemButton>
                </Tooltip>
              );
            })}
          </List>
        ))}
      </Box>

      <Box
        sx={{
          p: collapsed ? 0.85 : 1.15,
          flexShrink: 0,
        }}
      >
        {!collapsed && !isSmallHeight && <PaperLikeUpgradeCard />}

        <Divider
          sx={{
            my: collapsed ? 1 : 1.15,
            borderColor: THEME.border,
          }}
        />

        <Tooltip
          title={collapsed ? user.name || user.email || "Usuario" : ""}
          placement="right"
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              p: collapsed ? 0.2 : 0.75,
              borderRadius: "16px",
              bgcolor: collapsed ? "transparent" : "rgba(255,255,255,0.045)",
              border: collapsed ? "none" : `1px solid ${THEME.border}`,
              minWidth: 0,
            }}
          >
            <Avatar
              sx={{
                width: collapsed ? 36 : 38,
                height: collapsed ? 36 : 38,
                background:
                  "linear-gradient(135deg, rgba(79,156,249,0.95), rgba(211,125,54,0.95))",
                color: "#FFFFFF",
                fontWeight: 850,
                fontSize: collapsed ? "0.85rem" : "0.9rem",
                border: "1px solid rgba(255,255,255,0.18)",
                fontFamily: FONT_FAMILY,
              }}
            >
              {user.initials}
            </Avatar>

            {!collapsed && (
              <Box sx={{ ml: 1, minWidth: 0, flex: 1 }}>
                <Typography
                  noWrap
                  sx={{
                    color: THEME.text,
                    fontWeight: 700,
                    fontSize: "0.76rem",
                    lineHeight: 1.25,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {user.name || user.email || "Usuario"}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    color: THEME.muted,
                    fontSize: "0.66rem",
                    fontWeight: 560,
                    textTransform: "capitalize",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {user.roleLabel}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>

        <Tooltip title={collapsed ? "Cerrar sesión" : ""} placement="right" arrow>
          <StyledListItemButton
            collapsed={collapsed}
            onClick={handleLogout}
            sx={{
              mt: 0.8,
              mx: 0,

              "& .MuiListItemIcon-root": {
                color: THEME.danger,
                bgcolor: collapsed ? "rgba(251,113,133,0.08)" : THEME.dangerSoft,
                borderColor: "rgba(251,113,133,0.2)",
              },

              "& .MuiListItemText-primary": {
                color: THEME.danger,
              },

              "&:hover": {
                bgcolor: THEME.dangerSoft,

                "& .MuiListItemIcon-root": {
                  color: THEME.danger,
                  bgcolor: "rgba(251,113,133,0.18)",
                },
              },
            }}
          >
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>

            {!collapsed && <ListItemText primary="Cerrar sesión" />}
          </StyledListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { md: "none" },
          bgcolor: "rgba(7,17,31,0.9)",
          color: THEME.text,
          borderBottom: `1px solid ${THEME.border}`,
          backdropFilter: "blur(16px)",
          fontFamily: FONT_FAMILY,
        }}
      >
        <Toolbar sx={{ minHeight: "58px !important" }}>
          <IconButton
            color="inherit"
            onClick={() => setMobileOpen(true)}
            edge="start"
            sx={{
              mr: 1,
              width: 36,
              height: 36,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.055)",
              border: `1px solid ${THEME.border}`,
              "&:hover": {
                bgcolor: "rgba(79,156,249,0.14)",
              },
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Stack direction="row" spacing={1} alignItems="center">
<Box
  sx={{
    width: 34,
    height: 34,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    bgcolor: "#FFFFFF",
    border: "1px solid rgba(255,255,255,0.85)",
    overflow: "hidden",
    boxShadow: "0 10px 24px rgba(0,0,0,0.20)",
  }}
>
  <Box
    component="img"
    src="/images/logo_simbolo.png"
    alt="DQPA"
    sx={{
      width: 28,
      height: 28,
      objectFit: "contain",
      display: "block",
    }}
  />
</Box>

            <Typography
              sx={{
                fontWeight: 850,
                color: THEME.text,
                letterSpacing: "-0.035em",
                fontSize: "0.9rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              DQPA
            </Typography>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          overflow: "visible",

          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            borderRight: `1px solid ${THEME.border}`,
            bgcolor: THEME.sidebar,
            boxShadow: isMobile
              ? "0 24px 70px rgba(0,0,0,0.45)"
              : "0 0 34px rgba(0,0,0,0.18)",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

const PaperLikeUpgradeCard = () => (
  <Box
    sx={{
      p: 1.15,
      borderRadius: "17px",
      bgcolor: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(211,125,54,0.22)",
      position: "relative",
      overflow: "hidden",

      "&:before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "radial-gradient(circle at 15% 10%, rgba(211,125,54,0.20), transparent 38%), radial-gradient(circle at 90% 20%, rgba(79,156,249,0.14), transparent 40%)",
        pointerEvents: "none",
      },
    }}
  >
    <Stack
      direction="row"
      spacing={0.85}
      alignItems="center"
      sx={{ mb: 0.75, position: "relative" }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(211,125,54,0.14)",
          color: THEME.accent,
          border: "1px solid rgba(211,125,54,0.24)",
        }}
      >
        <AutoGraphIcon sx={{ fontSize: 18 }} />
      </Box>

      <Typography
        sx={{
          color: THEME.text,
          fontWeight: 760,
          fontSize: "0.76rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        Evalúa tus datos
      </Typography>
    </Stack>

    <Typography
      sx={{
        color: THEME.muted,
        fontWeight: 520,
        fontSize: "0.68rem",
        lineHeight: 1.45,
        position: "relative",
        fontFamily: FONT_FAMILY,
      }}
    >
      Completa el cuestionario y obtén el estado AS-IS de tu organización.
    </Typography>
  </Box>
);

export default Sidebar;
